import { Box, Divider, Grid, Link, Stack, Typography } from "@mui/material";
import styles from "./TakedownSection.module.css";
import { CoreButton, CoreCopyButton, CoreModal, CoreModalDescription } from "@verity/components/core";
import i18n from "../../../../i18n";
import RedAsterisk from "../CreateStandaloneAssessmentSection/red-asterisk.svg?react";
import { Trans } from "react-i18next";
import { useState } from "react";
import {
	ClickableQuestion,
	QuestionNumber,
	TakedownLink,
	TakedownNoResults,
	TakedownQuestion,
	getQuestionText,
} from "./TakedownSection";
import { useMobileLayout } from "@verity/components";
import { DMCAQuestion, DMCAQuestionSources } from ".";
import ReactDOMServer from "react-dom/server";

type DMCAEmailModalInfo = {
	email: string;
	url: string;
	question: string;
	dateScannedAt: string;
};

export function FrequentQuestionsTabpanel({
	questions,
	tabId,
	controlId,
}: {
	questions: DMCAQuestion[];
	tabId: string;
	controlId: string;
}) {
	const noResults = questions.length === 0;
	const isMobileLayout = useMobileLayout();
	return (
		<Box
			role="tabpanel"
			aria-labelledby={tabId}
			className={styles.takedownTabpanel}
			id={controlId}
			{...(noResults && {
				sx: { backgroundColor: "#f9f9f9" },
			})}
		>
			{noResults ? (
				<TakedownNoResults />
			) : (
				<Stack spacing={2}>
					<Stack spacing={0.7} padding={0.5}>
						<Typography variant="h3" className={styles.tabpanelTitle}>
							{i18n.t("portal.components.contentProtectionPageTSX.review.freqUsedQuestions.title")}
						</Typography>
						<Typography className={styles.tabpanelDescription}>
							{i18n.t("portal.components.contentProtectionPageTSX.review.freqUsedQuestions.description")}
						</Typography>
					</Stack>
					<Stack spacing={1.5}>
						{questions.map((question, index) => {
							const questionQuery = getQuestionText(question);
							const questionNumber = question.questionNumber;
							return questionQuery && questionNumber ? (
								<ClickableQuestion
									key={index}
									question={question}
									questionQuery={questionQuery}
									questionNumber={questionNumber}
									tabpanel="freq"
									isMobileLayout={isMobileLayout}
								>
									{question.sources && (
										<Stack spacing={1} paddingLeft={"8px"} alignItems={"flex-start"} width={"100%"}>
											<Typography color={"#6f6f6f"}>
												{i18n.t(
													"portal.components.contentProtectionPageTSX.review.freqUsedQuestions.viewTopXWebsites",
													{ number: question.sources.length },
												)}
											</Typography>
											{question.sources.map((source, index) => {
												return (
													<TakedownLink
														key={index}
														source={source.url}
														sourceNum={index + 1}
														questionNum={questionNumber}
													/>
												);
											})}
										</Stack>
									)}
								</ClickableQuestion>
							) : null;
						})}
					</Stack>
				</Stack>
			)}
		</Box>
	);
}

export function SafeQuestionTabpanel({
	questions,
	tabId,
	controlId,
}: {
	questions: DMCAQuestion[];
	tabId: string;
	controlId: string;
}) {
	const noResults = questions.length === 0;
	return (
		<Box
			role="tabpanel"
			aria-labelledby={tabId}
			className={styles.takedownTabpanel}
			id={controlId}
			{...(noResults && {
				sx: { backgroundColor: "#f9f9f9" },
			})}
		>
			{noResults ? (
				<TakedownNoResults />
			) : (
				<Stack spacing={1.5}>
					<Stack spacing={0.7} padding={0.5}>
						<Typography variant="h3" className={styles.tabpanelTitle}>
							{i18n.t("portal.components.contentProtectionPageTSX.review.safeQuestions.title")}
						</Typography>
						<Typography className={styles.tabpanelDescription}>
							{i18n.t("portal.components.contentProtectionPageTSX.review.safeQuestions.description")}
						</Typography>
					</Stack>
					<Stack spacing={1.5}>
						{questions.map((question, index) => {
							const questionQuery = getQuestionText(question);
							return questionQuery && question.questionNumber ? (
								<SafeQuestion key={index} questionQuery={questionQuery} questionNumber={question.questionNumber} />
							) : null;
						})}
					</Stack>
				</Stack>
			)}
		</Box>
	);
}

function SafeQuestion({ questionQuery, questionNumber }: { questionQuery: string; questionNumber: number }) {
	const isMobileLayout = useMobileLayout();
	return (
		<Stack
			direction={isMobileLayout ? "column" : "row"}
			spacing={isMobileLayout ? 1.5 : 1}
			className={`${styles.questionContainer} ${styles.isStatic}`}
		>
			<QuestionNumber questionNumber={questionNumber} />
			<TakedownQuestion question={questionQuery} />
		</Stack>
	);
}

export function CompromisedTabpanel({
	questions,
	tabId,
	controlId,
	lastFetchedDate,
}: {
	questions: DMCAQuestion[];
	tabId: string;
	controlId: string;
	lastFetchedDate: string | null;
}) {
	const noResults = questions.length === 0;
	const isMobileLayout = useMobileLayout();
	const [dmcaModalInfo, setDmcaModalInfo] = useState<DMCAEmailModalInfo | null>(null);

	return (
		<Box
			role="tabpanel"
			aria-labelledby={tabId}
			className={styles.takedownTabpanel}
			id={controlId}
			{...(noResults && {
				sx: { backgroundColor: "#f9f9f9" },
			})}
		>
			{noResults ? (
				<TakedownNoResults />
			) : (
				<>
					<Stack spacing={0.7} direction={"column"}>
						<Typography variant="h3" className={styles.tabpanelTitle}>
							{i18n.t("portal.components.contentProtectionPageTSX.review.compromisedQuestions.title")}
						</Typography>
						<Typography className={styles.tabpanelDescription}>
							{i18n.t("portal.components.contentProtectionPageTSX.review.compromisedQuestions.description")}
						</Typography>
						<Typography component="span" className={styles.disclaimer}>
							<RedAsterisk />
							<Trans
								i18nKey={"portal.components.contentProtectionPageTSX.review.compromisedQuestions.disclaimer"}
								components={{ b: <strong /> }}
							/>
						</Typography>
					</Stack>
					<Stack spacing={1.5}>
						{questions.map((question, index) => {
							const questionQuery = getQuestionText(question);
							const questionNumber = question.questionNumber;
							return questionQuery && questionNumber ? (
								<ClickableQuestion
									key={index}
									question={question}
									questionNumber={questionNumber}
									questionQuery={questionQuery}
									tabpanel="compromised"
									isMobileLayout={isMobileLayout}
								>
									{question.sources && (
										<CompromisedQuestionExpanded
											questionSources={question.sources}
											questionNumber={questionNumber}
											onTakedownRequest={setDmcaModalInfo}
											questionQuery={questionQuery}
											lastFetchedDate={lastFetchedDate}
											isMobileLayout={isMobileLayout}
										/>
									)}
								</ClickableQuestion>
							) : null;
						})}
					</Stack>
				</>
			)}
			{dmcaModalInfo && <DMCAEmailModal dmcaInfo={dmcaModalInfo} onClose={() => setDmcaModalInfo(null)} />}
		</Box>
	);
}

function CompromisedQuestionExpanded({
	questionSources,
	questionNumber,
	onTakedownRequest,
	questionQuery,
	lastFetchedDate,
	isMobileLayout,
}: {
	questionSources: DMCAQuestionSources[];
	questionNumber: number;
	onTakedownRequest: (dmcaEmailModalInfo: DMCAEmailModalInfo) => void;
	questionQuery: string;
	lastFetchedDate: string | null;
	isMobileLayout: boolean;
}) {
	if (questionSources.length === 0) return null;
	return questionSources.map((source, index) => {
		const numSources = questionSources.length;
		return (
			<Stack key={index} gap={1} width={"100%"}>
				<Stack
					direction={isMobileLayout ? "column" : "row"}
					justifyContent={isMobileLayout ? "flex-start" : "space-between"}
					width={"100%"}
					spacing={isMobileLayout ? 1 : 15}
					padding={"5px 0"}
				>
					<TakedownLink
						source={source.url}
						sourceNum={index + 1}
						questionNum={questionNumber}
						className={styles.compromisedLink}
					/>
					<Box
						minWidth={isMobileLayout ? "unset" : "260px"}
						maxWidth={isMobileLayout ? "unset" : "260px"}
						display={"flex"}
						justifyContent={"flex-end"}
					>
						{source.email && lastFetchedDate ? (
							<CoreButton
								className={styles.takedownButton}
								aria-haspopup="dialog"
								onClick={(e) => {
									e.stopPropagation();
									onTakedownRequest({
										email: source.email!,
										url: source.url,
										dateScannedAt: new Intl.DateTimeFormat(i18n.language, {
											month: "2-digit",
											day: "2-digit",
											year: "numeric",
										}).format(new Date(lastFetchedDate)),
										question: questionQuery,
									});
								}}
							>
								{i18n.t(
									"portal.components.contentProtectionPageTSX.review.compromisedQuestions.questionTakedownButton",
								)}
							</CoreButton>
						) : (
							<Typography component="span" className={styles.noEmailFoundText}>
								<Trans
									i18nKey="portal.components.contentProtectionPageTSX.review.compromisedQuestions.noEmail"
									components={{
										a: (
											<Link
												href="https://reportcontent.google.com/forms/dmca_search"
												target="_blank"
												rel="noopener noreferrer"
												aria-label={i18n.t(
													"portal.components.contentProtectionPageTSX.review.compromisedQuestions.googleReportFormAriaLabel",
												)}
											/>
										),
									}}
								/>
							</Typography>
						)}
					</Box>
				</Stack>
				{index !== numSources - 1 && <Divider />}
			</Stack>
		);
	});
}

function DMCAEmailBody({ dmcaInfo }: { dmcaInfo: DMCAEmailModalInfo }) {
	return (
		<Stack spacing={2} className={styles.messageContainer}>
			<Typography>{i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.emailGreetings")}</Typography>
			<Typography>
				<Trans
					i18nKey={"portal.components.contentProtectionPageTSX.review.dmcaModal.emailBody1"}
					components={{ b: <strong /> }}
					values={{ question: dmcaInfo.question }}
				/>
			</Typography>
			<ul style={{ paddingLeft: "25px" }}>
				<li>
					{i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.emailBody2", {
						question: dmcaInfo.question,
					})}
				</li>

				<li>
					<Trans
						i18nKey={"portal.components.contentProtectionPageTSX.review.dmcaModal.emailBody3"}
						values={{ questionURL: dmcaInfo.url }}
						components={{
							a: (
								<Link
									href={dmcaInfo.url}
									target="_blank"
									rel="noopener noreferrer"
									sx={{
										textDecorationColor: "#333333",
										color: "#333333",
										whiteSpace: "normal",
										wordBreak: "break-all",
									}}
								/>
							),
						}}
					/>
				</li>
			</ul>
			<Typography>
				{i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.emailBody4", {
					localizedDate: dmcaInfo.dateScannedAt,
				})}
			</Typography>
			<Stack spacing={0.5}>
				<Typography>{i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.emailConclusion")}</Typography>
				<Typography>{i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.emailClosing")}</Typography>
			</Stack>
		</Stack>
	);
}

function getPlainDmcaEmailText(dmcaInfo: DMCAEmailModalInfo): string {
	const texts: string[] = [
		i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.emailGreetings"),
		i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.emailBody1Plain", {
			question: dmcaInfo.question,
		}),
		i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.emailBody2", {
			question: dmcaInfo.question,
		}),
		i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.emailBody3Plain", {
			questionURL: dmcaInfo.url,
		}),
		i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.emailBody4", {
			localizedDate: dmcaInfo.dateScannedAt,
		}),
		i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.emailConclusion"),
		i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.emailClosing"),
	];

	return texts.join("\n\n");
}

function DMCAEmailModal({ dmcaInfo, onClose }: { dmcaInfo: DMCAEmailModalInfo; onClose: () => void }) {
	const dmcaEmailBody = <DMCAEmailBody dmcaInfo={dmcaInfo} />;
	const htmlEmail = ReactDOMServer.renderToStaticMarkup(dmcaEmailBody);
	const htmlPlainText = getPlainDmcaEmailText(dmcaInfo);

	return (
		<CoreModal
			title={i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.title")}
			open
			onClose={onClose}
			buttons={[
				{
					text: i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.cancelButton"),
					color: "secondary",
					onClick: onClose,
				},
			]}
			maxWidth="720px"
			padding="15px 20px"
		>
			<Stack spacing={2}>
				<CoreModalDescription>
					{i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.description")}
				</CoreModalDescription>
				<Stack spacing={1} className={styles.emailBody}>
					<Grid container rowGap={1}>
						<DMCACopyRow
							label={i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.emailAddress")}
							textToCopy={dmcaInfo.email}
						/>
						<DMCACopyRow
							label={i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.subject")}
							textToCopy={i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.subjectValue")}
						/>
						<DMCAMessageCopyRow
							label={i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.message")}
							htmlToCopy={htmlEmail}
							fallbackText={htmlPlainText}
						/>
					</Grid>
					{dmcaEmailBody}
				</Stack>
			</Stack>
		</CoreModal>
	);
}

function DMCACopyRow({
	label,
	textToCopy,
}: {
	/**Header or label. Shown in first column of grid */
	label: string;
	/**What gets copied when copy button is clicked */
	textToCopy: string;
}) {
	return (
		<Grid container alignItems="center" className={styles.copyRow}>
			<Grid item xs={12} sm={3}>
				<Typography className={styles.copyHeader}>{label}</Typography>
			</Grid>
			<Grid item xs={12} sm={9}>
				<Stack direction="row" alignItems="center" spacing={1}>
					<Typography>{textToCopy}</Typography>
					<CoreCopyButton
						text={textToCopy}
						className={styles.dmcaCopyButton}
						tooltip={i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.copyButtonTooltip", {
							value: label,
						})}
						copyMessageLocation="right"
					/>
				</Stack>
			</Grid>
		</Grid>
	);
}

function DMCAMessageCopyRow({
	label,
	htmlToCopy,
	fallbackText,
}: {
	/**Header or label. Shown in first column of grid */
	label: string;
	/**HTML that gets copied when copy button is clicked */
	htmlToCopy?: string;
	fallbackText: string;
}) {
	return (
		<Grid container alignItems="center" className={styles.copyRow} width={"100%"}>
			<Grid item xs={3}>
				<Typography className={styles.copyHeader}>{label}</Typography>
			</Grid>
			<Grid
				item
				xs={9}
				sx={{
					display: "flex",
					justifyContent: "flex-end",
				}}
			>
				{/* The CoreCopyButton logic (which is where the Stack is) */}
				<CoreCopyButton
					text={fallbackText}
					htmlText={htmlToCopy}
					className={styles.dmcaCopyButton}
					tooltip={i18n.t("portal.components.contentProtectionPageTSX.review.dmcaModal.copyButtonTooltip", {
						value: label,
					})}
					copyMessageLocation="left"
				/>
			</Grid>
		</Grid>
	);
}
